import { defineStore } from 'pinia'
import { computed, Ref, ref, watchEffect } from 'vue'
import { PaymentRequestTypeEnum, TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import { useTranslation } from '@/composables/translation'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'

export const useTimesheetStore = defineStore('payment-request-timesheet', () => {
  const { $t } = useTranslation()
  const paymentRequestStore = usePaymentRequestStore()

  const worksomeTimesheetId = ref('')
  const hasWorksomeTimesheet = ref('')
  const requireTimesheets = ref(false)
  const timesheetSpreadsheetValue = ref('')
  const hasTimesheetSpreadsheet = computed(() => !!timesheetSpreadsheetValue.value)
  const useWorksomeTimesheetRatherThanLegacy = computed(
    () =>
      paymentRequestStore.enableWorksomeTimesheetFeature &&
      (!hasTimesheetSpreadsheet.value || !!worksomeTimesheetId.value),
  )

  const timesheetTypeOptions = computed(() => {
    const options: { value: TimesheetTypeEnum; label: string }[] = []
    if (useWorksomeTimesheetRatherThanLegacy.value) {
      if (paymentRequestStore.eligibleForWorksomeTimesheet) {
        options.push({ value: TimesheetTypeEnum.TIMESHEET, label: $t('Timesheet') })
      }
    } else {
      options.push({ value: TimesheetTypeEnum.SHEET, label: $t('Timesheet') })
    }
    // Uplaod file and comment-based-timesheet is always available
    options.push({ value: TimesheetTypeEnum.TEXT, label: $t('Comment') })
    options.push({ value: TimesheetTypeEnum.FILE, label: $t('Upload file') })
    // 'None' is available if timesheets are not required
    // or even if they are required but the payment request is of type 'Specify amount'
    if (
      !requireTimesheets.value ||
      paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT
    ) {
      options.push({ value: TimesheetTypeEnum.NONE, label: $t('None') as string })
    }
    return options
  })
  const timesheetType = ref(timesheetTypeOptions.value[0])
  // When initially instantiated, this store has no prop controlling the
  // availability of the worksome timesheet
  watchEffect(() => {
    if (hasTimesheetSpreadsheet.value && !useWorksomeTimesheetRatherThanLegacy.value) {
      // @ts-ignore
      timesheetType.value = timesheetTypeOptions.value.find(
        (option) => option.value === TimesheetTypeEnum.SHEET,
      )
    } else if (!paymentRequestStore.paymentRequestId) {
      timesheetType.value = timesheetTypeOptions.value[0]
    }
  })
  const requireTimesheetConfirmation = computed(
    () =>
      paymentRequestStore.hasPayroll &&
      paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE &&
      timesheetType.value.value !== TimesheetTypeEnum.NONE,
  )

  const timesheetFiles: Ref<{ localId: number }[]> = ref([])
  const timesheetComment = ref('')

  const clearTimesheets = () => {
    timesheetType.value = timesheetTypeOptions.value[0]
    timesheetSpreadsheetValue.value = ''
    timesheetFiles.value = []
    timesheetComment.value = ''
  }

  const resetTimesheetOptions = () => {
    // if a timesheet comment exists, set timesheetType to text
    // always set to comment if payment request is of type 'Specify amount'
    if (
      paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT ||
      timesheetComment.value
    ) {
      timesheetType.value =
        timesheetTypeOptions.value.find((option) => option.value === TimesheetTypeEnum.TEXT) ||
        timesheetTypeOptions.value[0]
      return
    }
    timesheetType.value = timesheetTypeOptions.value[0]
  }

  return {
    worksomeTimesheetId,
    hasWorksomeTimesheet,
    timesheetSpreadsheetValue,
    timesheetFiles,
    timesheetComment,
    timesheetType,
    timesheetTypeOptions,
    requireTimesheets,
    requireTimesheetConfirmation,
    clearTimesheets,
    resetTimesheetOptions,
  }
})
